import { isJwtExpired } from '@/utils/jwt'
import { getRefreshedAccessToken } from '@/services/auth-refresh'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** Payload extra do corpo do erro além de `error.code`/`error.message` (ex: `errors` de validação por linha). */
  readonly details?: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  token?: string
  /**
   * Usado apenas pelo próprio `authService.refresh` para evitar que a
   * chamada de refresh dispare uma tentativa de refresh sobre si mesma em
   * caso de 401 (loop infinito). Nenhum outro chamador deve usar isto.
   */
  skipAuthRefresh?: boolean
}

export type BlobRequestOptions = Omit<RequestInit, 'body'> & {
  token?: string
  skipAuthRefresh?: boolean
}

// Em produção, apiUrl vem de window.__NAVEGA_CONFIG__ (injetado pelo server.mjs via /config.js).
// Em dev, apiUrl é vazio e o Vite proxy encaminha /api/* para o backend sem CORS.
function createApiClient(prefix: string) {
  const BASE_URL = (window.__NAVEGA_CONFIG__?.apiUrl ?? import.meta.env.VITE_API_URL ?? '') + prefix

  // Inspeciona uma cópia da resposta (via `response.clone()`) para decidir se
  // é um 401 de token expirado, sem consumir o body original — ele ainda
  // será lido normalmente pelo chamador (`request`/`requestFull`/etc.) depois.
  async function isExpiredTokenResponse(response: Response, token: string | undefined) {
    if (response.status !== 401) return false
    const isJson = response.headers.get('content-type')?.includes('application/json')
    const json = isJson
      ? await response
          .clone()
          .json()
          .catch(() => undefined)
      : undefined
    const code = json?.error?.code
    return code === 'TOKEN_EXPIRED' || Boolean(token && isJwtExpired(token))
  }

  // Renovação automática de sessão (RF05): se uma requisição autenticada
  // recebe 401 de token expirado, dispara um único refresh (single-flight,
  // ver `auth-refresh.ts`) e reexecuta a requisição original com o novo
  // access token. Se o refresh falhar, devolve a resposta 401 original para
  // o fluxo de erro de sempre tratar — a sessão já terá sido limpa e o
  // redirecionamento para `/login` já terá sido disparado por `auth-refresh.ts`.
  async function fetchWithAuthRefresh(
    attempt: (token: string | undefined) => Promise<Response>,
    token: string | undefined,
    skipAuthRefresh: boolean | undefined,
  ): Promise<Response> {
    const response = await attempt(token)

    if (skipAuthRefresh || !token || !(await isExpiredTokenResponse(response, token))) {
      return response
    }

    try {
      const newAccessToken = await getRefreshedAccessToken()
      return await attempt(newAccessToken)
    } catch {
      return response
    }
  }

  async function requestFull<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, token, headers, skipAuthRefresh, ...rest } = options

    const response = await fetchWithAuthRefresh(
      (authToken) =>
        fetch(`${BASE_URL}${path}`, {
          ...rest,
          headers: {
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            ...headers,
          },
          body: body !== undefined ? JSON.stringify(body) : undefined,
        }),
      token,
      skipAuthRefresh,
    )

    if (response.status === 204) return undefined as T

    const isJson = response.headers.get('content-type')?.includes('application/json')
    const json = isJson ? await response.json() : undefined

    if (!response.ok) {
      const err = json?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
      )
    }

    return (json ?? undefined) as T
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, token, headers, skipAuthRefresh, ...rest } = options

    const response = await fetchWithAuthRefresh(
      (authToken) =>
        fetch(`${BASE_URL}${path}`, {
          ...rest,
          headers: {
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            ...headers,
          },
          body: body !== undefined ? JSON.stringify(body) : undefined,
        }),
      token,
      skipAuthRefresh,
    )

    if (response.status === 204) return undefined as T

    const isJson = response.headers.get('content-type')?.includes('application/json')
    const json = isJson ? await response.json() : undefined

    if (!response.ok) {
      const err = json?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
        json?.errors,
      )
    }

    return (json?.data ?? undefined) as T
  }

  async function requestBlob(path: string, options: BlobRequestOptions = {}): Promise<Blob> {
    const { token, headers, skipAuthRefresh, ...rest } = options
    const response = await fetchWithAuthRefresh(
      (authToken) =>
        fetch(`${BASE_URL}${path}`, {
          ...rest,
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            ...headers,
          },
        }),
      token,
      skipAuthRefresh,
    )
    if (!response.ok) {
      const json = await response.json().catch(() => ({}))
      const err = json?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
      )
    }
    return response.blob()
  }

  async function requestMultipart<T>(
    path: string,
    body: FormData,
    options: BlobRequestOptions = {},
  ): Promise<T> {
    const { token, headers, skipAuthRefresh, ...rest } = options
    const response = await fetchWithAuthRefresh(
      (authToken) =>
        fetch(`${BASE_URL}${path}`, {
          ...rest,
          method: 'POST',
          // Content-Type must NOT be set manually for FormData — browser adds the boundary
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            ...headers,
          },
          body,
        }),
      token,
      skipAuthRefresh,
    )
    if (response.status === 204) return undefined as T
    const json = await response.json()
    if (!response.ok) {
      const err = json?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'UNKNOWN_ERROR',
        err.message ?? 'Erro inesperado',
        json?.errors,
      )
    }
    return json.data as T
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { method: 'GET', ...options }),

    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'POST', body, ...options }),

    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'PUT', body, ...options }),

    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { method: 'PATCH', body, ...options }),

    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { method: 'DELETE', ...options }),

    getFull: <T>(path: string, options?: RequestOptions) =>
      requestFull<T>(path, { method: 'GET', ...options }),

    getBlob: (path: string, options?: BlobRequestOptions) =>
      requestBlob(path, { method: 'GET', ...options }),

    postMultipart: <T>(path: string, body: FormData, options?: BlobRequestOptions) =>
      requestMultipart<T>(path, body, options),
  }
}

export const apiClient = createApiClient('/api/v1')
export const apiV2Client = createApiClient('/api/v2')
// api-navega-backoffice-nodejs registra as rotas de admin na raiz, sem prefixo de versão
// (ex: /admin/auth/login) — ver src/app.ts da API.
export const apiAdminClient = createApiClient('/admin')
// Sem prefixo: usado para endpoints públicos fora de /admin, ex: os
// `.well-known/*` de descoberta de configuração M2M (sem Authorization).
export const apiPublicClient = createApiClient('')
